/**
 * BudgetCompactTable — dense "Compact" Budget Boost view.
 *
 * A heatmap table (one row per asset): identity, price, recent form, expected
 * points, the per-bucket odds shaded by probability, and the headline expected
 * price change. Columns differ by tier, so the page renders one table per tier.
 * Scrolls horizontally on narrow screens with a sticky asset column.
 */

import { TrendingDown, TrendingUp, Users } from "lucide-react";
import { m } from "@/paraglide/messages";
import {
  type BudgetItem,
  columnPointsNeededLabel,
  getBudgetColumns,
  heatCellColor,
  isDriverCalc,
  resultMagnitude,
} from "../lib/budget-display";

interface BudgetCompactTableProps {
  items: BudgetItem[];
  isPredictionMode: boolean;
  /** Ids (driverId / constructorId) of the signed-in user's saved team,
   * highlighted so their own assets stand out. */
  myTeamIds?: Set<string> | null;
}

/** Results-known cell: a directional magnitude bar plus the actual change. */
function ResultCell({ delta, tier }: { delta: number; tier: "A" | "B" }) {
  const { magnitude } = resultMagnitude(delta, tier);
  const isRise = delta > 0;
  const isDrop = delta < 0;
  const fillPct = `${Math.round(magnitude * 100)}%`;
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="relative flex h-2.5 w-16 overflow-hidden rounded-full bg-muted/40">
        <div className="flex h-full w-1/2 justify-end">
          {isDrop && (
            <div
              className="h-full rounded-l-full"
              style={{ width: fillPct, backgroundColor: "var(--destructive)" }}
            />
          )}
        </div>
        <div className="flex h-full w-1/2 justify-start">
          {isRise && (
            <div
              className="h-full rounded-r-full"
              style={{ width: fillPct, backgroundColor: "var(--success)" }}
            />
          )}
        </div>
        <div
          className="absolute top-0 bottom-0 left-1/2 w-px bg-foreground/40"
          aria-hidden="true"
        />
      </div>
      <span
        className={`w-11 text-right font-mono text-xs font-bold tabular-nums ${
          isRise
            ? "text-green-400"
            : isDrop
              ? "text-red-500"
              : "text-foreground/60"
        }`}
      >
        {delta > 0 ? "+" : ""}
        {delta.toFixed(2)}
      </span>
    </div>
  );
}

export function BudgetCompactTable({
  items,
  isPredictionMode,
  myTeamIds = null,
}: BudgetCompactTableProps) {
  if (items.length === 0) return null;
  const columns = getBudgetColumns(items[0]);
  const ptsHeader = isPredictionMode
    ? m.f1_budget_xpts_short()
    : m.f1_budget_pts_short();

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="sticky left-0 z-10 bg-muted/40 px-3 py-2 text-left font-semibold backdrop-blur-sm">
              {m.f1_budget_col_asset()}
            </th>
            <th className="px-2 py-2 text-right font-semibold">
              {m.f1_budget_col_price()}
            </th>
            <th className="px-2 py-2 text-right font-semibold">R-2</th>
            <th className="px-2 py-2 text-right font-semibold">R-1</th>
            <th className="px-2 py-2 text-right font-semibold">{ptsHeader}</th>
            {isPredictionMode &&
              columns.map((col) => (
                <th
                  key={col}
                  className={`px-2 py-2 text-center font-mono font-bold ${
                    col.startsWith("+") ? "text-success" : "text-destructive"
                  }`}
                >
                  {col}
                </th>
              ))}
            <th
              className={`px-3 py-2 text-right font-semibold ${
                isPredictionMode ? "" : "min-w-[8rem]"
              }`}
            >
              {isPredictionMode
                ? m.f1_budget_col_xdelta()
                : m.f1_budget_final_change()}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const code = isDriverCalc(item)
              ? item.driverCode
              : item.constructorCode;
            const name = isDriverCalc(item)
              ? item.driverName
              : item.constructorName;
            const delta = item.expectedPriceDelta;
            const deltaColor =
              delta > 0
                ? "text-success"
                : delta < 0
                  ? "text-destructive"
                  : "text-foreground/60";
            const DeltaIcon =
              delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : null;
            return (
              <tr
                key={isDriverCalc(item) ? item.driverId : item.constructorId}
                className={`${i % 2 === 1 ? "bg-muted/10" : ""} hover:bg-muted/25 transition-colors`}
              >
                <th
                  scope="row"
                  className={`sticky left-0 z-10 px-3 py-2 text-left ${
                    i % 2 === 1 ? "bg-card" : "bg-card/95"
                  } backdrop-blur-sm`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className="h-6 w-1 shrink-0 rounded-full"
                      style={{ backgroundColor: item.constructorColor }}
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="flex items-center gap-1 font-mono text-xs font-bold">
                        {code}
                        {myTeamIds?.has(
                          isDriverCalc(item)
                            ? item.driverId
                            : item.constructorId,
                        ) && (
                          <Users
                            className="h-3 w-3 shrink-0 text-primary"
                            aria-label={m.f1_myteam_in_team()}
                          >
                            <title>{m.f1_myteam_in_team()}</title>
                          </Users>
                        )}
                      </span>
                      <span className="block max-w-[7rem] truncate text-[11px] text-foreground/55">
                        {name}
                      </span>
                    </span>
                  </span>
                </th>
                <td className="px-2 py-2 text-right font-mono text-xs font-semibold tabular-nums">
                  ${item.currentPrice.toFixed(1)}
                </td>
                <td className="px-2 py-2 text-right font-mono text-xs tabular-nums text-foreground/60">
                  {item.pointsRMinus2 !== null
                    ? parseFloat(Number(item.pointsRMinus2).toFixed(1))
                    : "—"}
                </td>
                <td className="px-2 py-2 text-right font-mono text-xs tabular-nums text-foreground/60">
                  {item.pointsRMinus1 !== null
                    ? parseFloat(Number(item.pointsRMinus1).toFixed(1))
                    : "—"}
                </td>
                <td
                  className={`px-2 py-2 text-right font-mono text-xs tabular-nums ${
                    isPredictionMode ? "text-amber-500" : "text-primary"
                  }`}
                >
                  {item.expectedPoints.toFixed(1)}
                </td>
                {isPredictionMode &&
                  columns.map((col) => {
                    const probability = item.priceChangeProbabilities[col] || 0;
                    const ptsLabel = columnPointsNeededLabel(
                      col,
                      item.currentPrice,
                      item.pointsRMinus2,
                      item.pointsRMinus1,
                    );
                    return (
                      <td
                        key={col}
                        className="px-2 py-1.5 text-center align-middle"
                        style={{
                          backgroundColor: heatCellColor(col, probability),
                        }}
                        title={
                          ptsLabel
                            ? `${ptsLabel} ${m.f1_budget_pts_unit()}`
                            : undefined
                        }
                      >
                        <span className="block font-mono text-xs font-bold tabular-nums">
                          {probability}%
                        </span>
                      </td>
                    );
                  })}
                <td className="px-3 py-2">
                  {isPredictionMode ? (
                    <span
                      className={`flex items-center justify-end gap-0.5 font-mono text-xs font-bold tabular-nums ${deltaColor}`}
                    >
                      {DeltaIcon && <DeltaIcon className="h-3 w-3" />}
                      {delta > 0 ? "+" : ""}
                      {delta.toFixed(2)}
                    </span>
                  ) : (
                    <ResultCell delta={delta} tier={item.tier} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
